import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { QuantityCampaignDetails, ShopItemModel } from 'src/app/api/models/shop-item.model';
import { DialogData, ProductBonusModalComponent } from '../product-bonus-modal/product-bonus-modal.component';
import { ProfileDataRequest } from 'src/app/api/models/perfil';
import { Subscription } from 'rxjs';
import { CheckoutLineModel } from 'src/app/api/models/checkout';
import { CheckoutDialogComponent } from '../checkout-dialog/checkout-dialog.component';
import { PerfilService } from 'src/app/api/services/perfil.service';

@Component({
    selector: 'app-checkout-products-campaigns',
    templateUrl: './checkout-products-campaigns.component.html',
    styleUrls: ['./checkout-products-campaigns.component.css'],
    standalone: false
})

export class CheckoutProductsCampaignsComponent implements OnInit, OnDestroy{
  @Output() isModalOpen = new EventEmitter<boolean>();
  @Input() currentUser!: ProfileDataRequest;
  unsubscribe: Subscription = new Subscription();
  isLoading: boolean = true;
  productsLoaded: boolean = false;
  productsQuantitiesLoaded: boolean = false;
  originalQuantities = new Map<string, number>();
  originalProductsList: CheckoutLineModel[] = [];
  user!: ProfileDataRequest;

  constructor(
    public dialogRef: MatDialogRef<CheckoutProductsCampaignsComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: { products: CheckoutLineModel[]},
    public dialog: MatDialog,
    private perfilService: PerfilService
  ) {
  }

  ngOnInit() {
    this.user = this.perfilService.getCurrentUserInfo();

    for (const product of this.data.products) {
      this.originalQuantities.set(product.referencia, product.qtd || 0);
    }

    this.originalProductsList = this.data.products.map(p => ({ ...p })); // deep copy if needed

    // Deep clone the products so changes don't affect the parent
    this.data.products = this.data.products.map(p => ({
      ...structuredClone(p),
      bonusProductInfo: p.bonusProductInfo?.map(b => ({ ...b, bonusTiers: [...b.bonusTiers] })) ?? []
    }));
  }

  removeQuantityClick(shopItem: CheckoutLineModel): void {
    if(this.user.role === 'Laboratorio'){
        if (shopItem.qtd - 1 < 0) {
          this.OpenDeleteConfirmationModal(shopItem);
          return;
        } else {
          shopItem.qtd--;
        }
      }else{
        if (shopItem.qtd - 1 <= 0) {
          this.OpenDeleteConfirmationModal(shopItem);
          return;
        } else {
          shopItem.qtd--;
        }   
    }

    this.checkAndUpdateBonusQuantity(shopItem);
  }

  // FUNCTION : Opens the modal to confirm the deletion of the line
  OpenDeleteConfirmationModal(shopItem: CheckoutLineModel) {
    const dialogRef = this.dialog.open(CheckoutDialogComponent, {
      width: '350px',
    });

    this.unsubscribe.add(
      dialogRef.afterClosed().subscribe((result: boolean) => {
        if (result) {
          this.data.products = this.data.products.filter(p => p !== shopItem);
        }
      })
    );
  }

  onKeyDown(event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    if (+currentValue >= 1000 && !this.isControlKey(event)) {
      event.preventDefault();
    }
  }

  isControlKey(event: KeyboardEvent): boolean {
    const controlKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab'];
    return controlKeys.includes(event.key);
  }

  onEnterPress(inputElement: HTMLInputElement): void {
    inputElement.blur();
  }

  changeQuantity(newValue: string, shopItem: CheckoutLineModel, oldValue: number): void {
    const parsed = parseInt(newValue, 10);

    if (isNaN(parsed)) {
      shopItem.qtd = oldValue;
    } else {
      if (this.user.role === 'Laboratorio') {
        if (parsed < 0) {
          this.OpenDeleteConfirmationModal(shopItem);
          return;
        } else {
          shopItem.qtd = Math.min(parsed, 1000);
        }
      } else {
        if (parsed <= 0) {
          this.OpenDeleteConfirmationModal(shopItem);
          return;
        } else {
          shopItem.qtd = Math.min(parsed, 1000);
        }
      }
    }

    this.checkAndUpdateBonusQuantity(shopItem);
  }

  addQuantityClick(shopItem: CheckoutLineModel): void {
    if (!shopItem.qtd) {
      shopItem.qtd = 0;
    }
    if (shopItem.qtd < 1000) {
      shopItem.qtd += 1;
    }

    this.checkAndUpdateBonusQuantity(shopItem);
  }

  hasActiveQuantityCampaign(product: CheckoutLineModel): boolean {
    return product?.quantityCampaign?.some(campaign => 
      campaign.quantity >= 0 && campaign.discountPercentage > 0
    ) ?? false;
  }

  openProductBonus(product: CheckoutLineModel): void {
    this.isModalOpen.emit(true);

    const shopItem = this.convertCheckoutLineToShopItem(product);
    
    var dataToSend: DialogData = {
      bonusData: shopItem,
      addButton: true,
      processChanges: false
    };
    const dialogRef = this.dialog.open(ProductBonusModalComponent, {
        data: dataToSend
    });
    
    this.unsubscribe.add(
      dialogRef.afterClosed().subscribe((result) => {
        this.isModalOpen.emit(false);
        if (result?.reference && result?.quantityToAdd) {
          const matchedProduct = this.data.products.find(p => p.referencia === result.reference);
          if (matchedProduct) {
            matchedProduct.qtd = (matchedProduct.qtd || 0) + result.quantityToAdd;
            
            this.checkAndUpdateBonusQuantity(matchedProduct);
          }
        }
      })
    );
  }

  convertCheckoutLineToShopItem(line: CheckoutLineModel): ShopItemModel {
    const bonusList =
      (line.bonusProductInfo || []).flatMap(info =>
        (info.bonusTiers || []).map(tier => ({
          referenceBonus: info.bonusProductReference,
          description: info.bonusProductName,
          bonus: `${tier.requiredProductQuantity}+${tier.bonusProductQuantityGiven}`,
          imageBonus: info.imageBonus || ''
        }))
      );

    return {
      reference: line.referencia,
      decription: line.memoria,
      productImage: line.imagem,
      stock: line.stock,
      oldPrice: line.valor,
      currentPrice: line.valor_Liquido,
      class: line.color?.toString() ?? '',
      group: line.grupo,
      atc: line.atc,
      lab: line.marca_Laboratorio,
      points: line.pontos,
      iva: line.iva.toString(),
      family: line.familia,
      subfamily: [], 
      activeSubstance: [], 
      species: '',
      situacao: line.dispensa,
      favorite: false,
      imageId: '',
      productQuantity: line.qtd,
      isPoints: line.usePoints,
      request: '',
      validate: new Date(),
      color: Number(line.color),
      files: '',
      bonus: bonusList, 
      quantityCampaign: line.quantityCampaign,
      discountPercentage: line.discountPercentage,
      labDiscount: line.labDiscount,
      otherDiscount: line.otherDiscount
    };
  }

  getApplicableCampaign(line: { quantityCampaign?: QuantityCampaignDetails[]; qtd: number }): QuantityCampaignDetails | undefined {   
    return line.quantityCampaign?.find(campaign => 
        line.qtd > campaign.quantity && 
        (campaign.maxQuantity === 0 || line.qtd <= campaign.maxQuantity) &&
        campaign.discountPercentage > 0 
    );
  }  

  getLabDiscountPrice(line: any): number {  
    return line.valor * (1 - (line.labDiscount ?? 0) / 100);
  }

  getCampaignDiscountPrice(line: any, campaign: any): number {
    const labPrice = this.getLabDiscountPrice(line);
    return labPrice * (1 - (campaign?.discountPercentage ?? 0) / 100);
  }

  getBio2DiscountPrice(line: any): number {
    const campaignPrice = this.getCampaignDiscountPrice(line, this.getApplicableCampaign(line));
    return campaignPrice * (1 - (line.discountPercentage ?? 0) / 100);
  }
  
  getFinalDiscountedPrice(line: any): number {
    const bio2Price = this.getBio2DiscountPrice(line);
    return bio2Price * (1 - (line.otherDiscount ?? 0) / 100);
  }  

  getFinalDiscountedPrice2(line: any): number {
    let price = line.valor;
  
    // Apply Lab Discount
    if (line.labDiscount) {
      price *= (1 - (line.labDiscount / 100));
    }
  
    // Apply Campaign Discount
    const campaign = this.getApplicableCampaign(line);
    if (campaign?.discountPercentage) {
      price *= (1 - (campaign.discountPercentage / 100));
    }
  
    // Apply Bio2 Discount
    if (line.discountPercentage) {
      price *= (1 - (line.discountPercentage / 100));
    }
  
    // Apply Other Discount
    if (line.otherDiscount) {
      price *= (1 - (line.otherDiscount / 100));
    }
  
    return price;
  }

  getFormattedTotal(line: { qtd: number }): string {
    const finalUnitPrice = this.getFinalDiscountedPrice2(line);
    const total = finalUnitPrice * line.qtd;
    return total.toFixed(2);
  }


  // Checks if line has a bonus product and if it does, checks and updates the bonus quantity to receive
  checkAndUpdateBonusQuantity(product: CheckoutLineModel): void {
    if (!product?.bonusProductInfo) return;

    product.bonusProductInfo.forEach((bonusInfo) => {

      let totalProductQuantity = product.qtd ?? 0;

      // Sort tiers in descending order
      const sortedTiers = [...bonusInfo.bonusTiers].sort(
        (a, b) => b.requiredProductQuantity - a.requiredProductQuantity
      );

      let remainingQty = totalProductQuantity;
      let totalBonusQuantity = 0;

      sortedTiers.forEach((tier) => {
        const applicableBonusCount = Math.floor(remainingQty / tier.requiredProductQuantity);
        if (applicableBonusCount > 0) {
          totalBonusQuantity += applicableBonusCount * tier.bonusProductQuantityGiven;
          remainingQty -= applicableBonusCount * tier.requiredProductQuantity;
        }
      });

      // Update bonus
      bonusInfo.bonusAccumulated = totalBonusQuantity;
    });

    // Force table update
    this.data.products = [...this.data.products];
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onConfirm(): void {
    const productsWithChangedQuantity: CheckoutLineModel[] = [];

    // Products that had quantity changed
    this.data.products.forEach(product => {
      const originalQtd = this.originalQuantities.get(product.referencia) || 0;
      if ((product.qtd ?? 0) !== originalQtd) {
        productsWithChangedQuantity.push(product);
      }
    });
    
    // Products that were removed (present in original but not in current list)
    this.originalProductsList.forEach(originalProduct => {
      const stillExists = this.data.products.some(p => p.referencia === originalProduct.referencia);
      if (!stillExists) {
        productsWithChangedQuantity.push({
          ...originalProduct,
          qtd: 0
        });
      }
    });

    this.dialogRef.close({
      confirmed: true,
      products: productsWithChangedQuantity
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }
}
